// ============ PAGES: Human Design / Allocation / Auth ============
import React, { useState, useMemo } from 'react';
import { HD_TYPES, fmtNum, ASSETS, mmRng } from '../data';
import { SecHead, Donut } from '../components/core';
import { PremiumGate } from '../components/landing';
import { computeHD, birthToUTC, GATE_TH, GATE_EN, PHS_TH, CENTER_GATES } from '../lib/humandesign';
import { RaveBodygraph, DESIGN_C, PERSONALITY_C } from '../components/bodygraph';
import { BaziPanel } from '../components/destiny';
import { IChingPanel } from '../components/ichingpanel';
import { HdStructure } from '../components/hdstructure';
import { HdDetail } from '../components/hddetail';
import { PROFILE_READ, CHANNEL_READ, channelKey } from '../lib/destinyRead';
import { authSignup, authLogin, lineLoginUrl, googleLoginUrl, saveProfile } from '../services/api';
import { useT, useLang } from '../lib/i18n';

// Mirrors MIN_PASSWORD in server/auth.mjs, which is the one that actually
// enforces it — this copy only exists so the form can say so before the round
// trip. If you change one, change the other, or signups fail server-side with
// a message the form already promised would not happen.
const MIN_PASSWORD = 10;

// Allocation category labels (keys stay canonical Thai for logic/colors/picks).
const ALLOC_EN: Record<string, string> = {
  'หุ้นเติบโต': 'Growth stocks', 'หุ้นปันผล': 'Dividend stocks', 'ETF ดัชนี': 'Index ETFs',
  'ตราสารหนี้': 'Bonds', 'Crypto': 'Crypto', 'ทอง': 'Gold',
};

const ALLOC_COLORS: Record<string, string> = { 'หุ้นเติบโต': '#00e0b0', 'หุ้นปันผล': '#37c8f0', 'ETF ดัชนี': '#8b5cf6', 'ตราสารหนี้': '#5a6acf', 'Crypto': '#ff3b5c', 'ทอง': '#f0c75a' };

const ALLOC_PICKS: Record<string, string[]> = {
  'หุ้นเติบโต': ['NVDA', 'MSFT', 'AMZN', 'DELTA'],
  'หุ้นปันผล': ['PTT', 'KBANK', 'CPALL', 'AAPL'],
  'ETF ดัชนี': ['SPY', 'QQQ'],
  'ตราสารหนี้': ['US10Y', 'TGB10Y'],
  'Crypto': ['BTC', 'ETH', 'SOL'],
  'ทอง': ['GOLD'],
};

// ---------- BODYGRAPH ----------
const CENTERS = [
  { id: 'head', shape: 'tri-up', x: 100, y: 26, label: 'Head' },
  { id: 'ajna', shape: 'tri-down', x: 100, y: 74, label: 'Ajna' },
  { id: 'throat', shape: 'rect', x: 100, y: 128, label: 'Throat' },
  { id: 'g', shape: 'diamond', x: 100, y: 188, label: 'G Center' },
  { id: 'heart', shape: 'tri-up', x: 146, y: 168, label: 'Heart', small: true },
  { id: 'splenic', shape: 'tri-right', x: 38, y: 248, label: 'Spleen' },
  { id: 'sacral', shape: 'rect', x: 100, y: 252, label: 'Sacral' },
  { id: 'solar', shape: 'tri-left', x: 162, y: 248, label: 'Solar' },
  { id: 'root', shape: 'rect', x: 100, y: 312, label: 'Root' },
];
const CHANNELS = [
  ['head', 'ajna'], ['ajna', 'throat'], ['throat', 'g'], ['g', 'sacral'],
  ['sacral', 'root'], ['throat', 'heart'], ['heart', 'g'], ['splenic', 'sacral'],
  ['solar', 'sacral'], ['splenic', 'root'], ['solar', 'root'], ['g', 'splenic'],
];

function centerShape(c: any, active: boolean, color: string) {
  const s = c.small ? 14 : 20;
  const fill = active ? color : 'rgba(255,255,255,0.05)';
  const stroke = active ? color : 'rgba(255,255,255,0.22)';
  const common = { fill, stroke, strokeWidth: 1.4, className: active ? 'mm-bg-center on' : 'mm-bg-center' };
  switch (c.shape) {
    case 'tri-up': return <polygon points={`${c.x},${c.y - s} ${c.x - s},${c.y + s * 0.8} ${c.x + s},${c.y + s * 0.8}`} {...common}></polygon>;
    case 'tri-down': return <polygon points={`${c.x},${c.y + s} ${c.x - s},${c.y - s * 0.8} ${c.x + s},${c.y - s * 0.8}`} {...common}></polygon>;
    case 'tri-right': return <polygon points={`${c.x + s},${c.y} ${c.x - s * 0.8},${c.y - s} ${c.x - s * 0.8},${c.y + s}`} {...common}></polygon>;
    case 'tri-left': return <polygon points={`${c.x - s},${c.y} ${c.x + s * 0.8},${c.y - s} ${c.x + s * 0.8},${c.y + s}`} {...common}></polygon>;
    case 'diamond': return <polygon points={`${c.x},${c.y - s} ${c.x + s},${c.y} ${c.x},${c.y + s} ${c.x - s},${c.y}`} {...common}></polygon>;
    default: return <rect x={c.x - s} y={c.y - s * 0.75} width={s * 2} height={s * 1.5} rx="3" {...common}></rect>;
  }
}

export function Bodygraph({ typeKey, centers }: { typeKey: string; centers?: string[] }) {
  const t = HD_TYPES[typeKey];
  const activeSet = new Set((centers && centers.length ? centers : t.centers) as string[]);
  return (
    <svg viewBox="0 0 200 348" className="mm-bodygraph" role="img" aria-label={`Bodygraph: ${t.name}`}>
      {CHANNELS.map(([a, b], i) => {
        const ca = CENTERS.find(c => c.id === a)!, cb = CENTERS.find(c => c.id === b)!;
        const on = activeSet.has(a) && activeSet.has(b);
        return <line key={i} x1={ca.x} y1={ca.y} x2={cb.x} y2={cb.y}
          stroke={on ? t.color : 'rgba(255,255,255,0.10)'} strokeWidth={on ? 2.4 : 1.2}
          className={on ? 'mm-bg-chan on' : 'mm-bg-chan'} style={{ animationDelay: `${i * 60}ms` }}></line>;
      })}
      {CENTERS.map(c => (
        <g key={c.id}>
          {centerShape(c, activeSet.has(c.id), t.color)}
          <text x={c.x + (c.id === 'heart' ? 24 : c.id === 'splenic' ? -4 : c.id === 'solar' ? 4 : 30)}
            y={c.y + (c.id === 'splenic' ? 26 : c.id === 'solar' ? 26 : 4)}
            textAnchor={c.id === 'splenic' || c.id === 'solar' ? 'middle' : 'start'}
            className="mm-bg-lbl">{c.label}</text>
        </g>
      ))}
    </svg>
  );
}

// ---------- HUMAN DESIGN PAGE ----------
const HD_PLANETS = [
  { k: 'sun', sym: '☉', th: 'ดวงอาทิตย์', en: 'Sun' },
  { k: 'earth', sym: '⊕', th: 'โลก', en: 'Earth' },
  { k: 'nnode', sym: '☊', th: 'นอร์ทโหนด', en: 'North Node' },
  { k: 'snode', sym: '☋', th: 'เซาท์โหนด', en: 'South Node' },
  { k: 'moon', sym: '☽', th: 'ดวงจันทร์', en: 'Moon' },
  { k: 'mercury', sym: '☿', th: 'พุธ', en: 'Mercury' },
  { k: 'venus', sym: '♀', th: 'ศุกร์', en: 'Venus' },
  { k: 'mars', sym: '♂', th: 'อังคาร', en: 'Mars' },
  { k: 'jupiter', sym: '♃', th: 'พฤหัส', en: 'Jupiter' },
  { k: 'saturn', sym: '♄', th: 'เสาร์', en: 'Saturn' },
  { k: 'uranus', sym: '♅', th: 'ยูเรนัส', en: 'Uranus' },
  { k: 'neptune', sym: '♆', th: 'เนปจูน', en: 'Neptune' },
  { k: 'pluto', sym: '♇', th: 'พลูโต', en: 'Pluto' },
];
const HD_AUTH: Record<string, string> = { generator: 'Sacral Authority', mgen: 'Sacral Authority', projector: 'Splenic Authority', manifestor: 'Emotional Authority', reflector: 'Lunar Authority' };
const HD_DEF: Record<string, string> = { generator: 'Single Definition', mgen: 'Split Definition', projector: 'Single Definition', manifestor: 'Triple Split', reflector: 'No Definition' };
const HD_SIG: Record<string, string> = { generator: 'ความพึงพอใจ (Satisfaction)', mgen: 'ความพึงพอใจ + สงบ', projector: 'ความสำเร็จ (Success)', manifestor: 'ความสงบ (Peace)', reflector: 'ความประหลาดใจ (Surprise)' };
const HD_NOTSELF: Record<string, string> = { generator: 'ความขับข้องใจ (Frustration)', mgen: 'ขับข้องใจ + โกรธ', projector: 'ความขมขื่น (Bitterness)', manifestor: 'ความโกรธ (Anger)', reflector: 'ความผิดหวัง (Disappointment)' };
const HD_CROSS = ['Right Angle Cross of the Sphinx', 'Right Angle Cross of Eden', 'Left Angle Cross of Defiance', 'Juxtaposition Cross of Vitality', 'Right Angle Cross of the Four Ways', 'Left Angle Cross of Migration'];

// `genHDChart` lived here and invented a chart from a seeded RNG — random gates,
// a random Incarnation Cross, a random Digestion and Environment. It was already
// dead code (the page reads the real ephemeris), and its Variables are now
// computed properly in humandesign.ts, so it is gone rather than left to be
// re-wired by accident.

/**
 * What each open (undefined) centre does to an investor. This is the half of a
 * chart people actually act from: an open centre takes in and amplifies whatever
 * is around it, which in a market means other people's certainty and other
 * people's panic. Defined centres are the consistent part you can lean on.
 */
const OPEN_CENTER_READ: Record<string, { th: string; en: string }> = {
  head: { th: 'รับแรงกดดัน "ต้องคิดให้ออก" จากข่าวและคนอื่น — คำถามที่ไม่ใช่ของคุณจะดูดเวลาไปทั้งวัน', en: 'Takes in mental pressure to solve things that are not yours to solve — questions from the feed eat the day.' },
  ajna: { th: 'ยืมความมั่นใจของคนอื่นมาเป็นความคิดตัวเอง — ระวังการ "แน่ใจ" หลังอ่านบทวิเคราะห์คนเดียว', en: 'Borrows other people’s certainty as your own view — beware feeling sure right after one analyst’s take.' },
  throat: { th: 'อยากพูด/ประกาศพอร์ตเพื่อเรียกความสนใจ — การป่าวประกาศไม้ทำให้ตัดขาดทุนยากขึ้น', en: 'Pushes you to announce positions for attention — a broadcast trade is a trade you cannot cut.' },
  g: { th: 'ตัวตนและทิศทางแกว่งตามสิ่งแวดล้อม — สไตล์การเทรดจะเปลี่ยนตามคนที่คุยด้วยล่าสุด', en: 'Identity and direction shift with the room — your style follows whoever you spoke to last.' },
  heart: { th: 'พยายามพิสูจน์คุณค่าตัวเองด้วยผลตอบแทน — ที่มาของการเพิ่มไม้เพื่อ "เอาคืน"', en: 'Tries to prove worth through returns — this is where revenge sizing comes from.' },
  splenic: { th: 'เกาะสิ่งที่ไม่ดีต่อตัวเองเพราะกลัวปล่อย — ถือของเน่าไว้นานเกินไป', en: 'Holds on to what is not good for you out of fear of letting go — dead positions live too long.' },
  sacral: { th: 'ไม่รู้ว่าพอเมื่อไหร่ ทำงาน/เฝ้าจอเกินพลังตัวเอง — เทรดเกินจำนวนไม้ที่ควร', en: 'Does not know when enough is enough — overtrades and overwatches the screen.' },
  solar: { th: 'ดูดอารมณ์ตลาดเข้ามาเต็ม ๆ — วันตลาดกลัว คุณจะกลัวแรงกว่าตลาด', en: 'Absorbs and amplifies the market’s mood — on a fearful day you will be more afraid than the tape.' },
  root: { th: 'รับแรงเร่ง "ต้องรีบ" — เข้าไม้เพราะกลัวตกรถมากกว่าเพราะสัญญาณ', en: 'Takes in adrenaline and hurry — you enter because you feel late, not because of a signal.' },
};

/** How each Authority should actually make the decision to press the button. */
const AUTH_PROTOCOL: Record<string, { th: string; en: string }> = {
  Emotional: { th: 'ห้ามตัดสินใจในคลื่นอารมณ์ — รอให้ผ่านอย่างน้อยหนึ่งคืน แล้วดูว่ายังอยากทำอยู่ไหม ความชัดเจนมาจากเวลา ไม่ใช่ข้อมูลเพิ่ม', en: 'Never decide on the wave. Sleep on it at least one night and see if you still want it. Clarity comes from time, not more data.' },
  Sacral: { th: 'ถามตัวเองเป็นคำถามใช่/ไม่ใช่ แล้วฟังเสียงตอบจากท้อง ไม่ใช่หัว — ถ้าไม่มีแรงตอบ "อื้อ" แปลว่าไม่ใช่', en: 'Ask a yes/no question and listen for the gut response, not the head. No lift in the body means no.' },
  Splenic: { th: 'สัญชาตญาณพูดครั้งเดียวและเบามาก — ถ้าลังเลแล้วกลับไปคิดใหม่ คุณกำลังใช้หัวแทนม้าม', en: 'The spleen speaks once, quietly. If you go back to re-think it, you have switched to the mind.' },
  Ego: { th: 'ถามว่า "ฉันอยากได้จริงไหม" และมีแรงพอจะทำจนจบไหม — สัญญาที่ใจไม่อยากทำจะถูกทิ้งกลางทาง', en: 'Ask whether you truly want it and have the will to finish. A commitment the heart refuses gets abandoned.' },
  'Self-Projected': { th: 'ต้องได้ยินตัวเองพูดออกมา — เล่าแผนให้คนที่ฟังเป็น (ไม่ให้คำแนะนำ) แล้วฟังน้ำเสียงตัวเอง', en: 'You must hear yourself. Talk the plan out to someone who listens without advising, then listen to your own voice.' },
  Mental: { th: 'คุยกับคนที่ไว้ใจหลายคนเพื่อ "คิดออกเสียง" และเลือกสภาพแวดล้อมให้ถูก — สภาพแวดล้อมสำคัญกว่าข้อมูล', en: 'Talk it through with trusted people to think out loud, and get the environment right — environment matters more than data.' },
  Lunar: { th: 'รอครบรอบจันทรคติ ~28 วันก่อนตัดสินใจใหญ่ — สิ่งที่ยังจริงหลังครบรอบคือของจริง', en: 'Wait a full lunar cycle (~28 days) before a big decision. What is still true after the cycle is true.' },
};

export function HumanDesignPage({ user, setUser, onNav, onUpgrade, onAuth }: any) {
  const tr = useT();
  const { lang } = useLang();
  const [birth, setBirth] = useState(user?.birth || '1992-08-15');
  const [time, setTime] = useState(user?.btime || '08:30');
  const [place, setPlace] = useState(user?.place || 'กรุงเทพมหานคร');
  const [tz, setTz] = useState<number>(user?.tz ?? 7);
  const [revealed, setRevealed] = useState(!!user?.hdType);
  const [tab, setTab] = useState<'hd' | 'bazi' | 'iching'>('hd');
  // One birth instant, three readings — HD, BaZi and I Ching all resolve from it.
  const birthUTC = useMemo(() => birthToUTC(birth, time, tz), [birth, time, tz]);
  const hd = useMemo(() => computeHD(birthUTC), [birthUTC]);
  const typeKey = hd.typeKey;
  const t = HD_TYPES[typeKey];
  const chart = {
    authority: hd.authority, definition: hd.definition, profile: hd.profile, profileName: hd.profileName,
    cross: hd.cross, signature: HD_SIG[typeKey], notSelf: HD_NOTSELF[typeKey],
    personality: hd.personality, design: hd.design,
  };

  const designGates = useMemo(() => hd.design.map((a: any) => a.gate), [hd]);
  const persGates = useMemo(() => hd.personality.map((a: any) => a.gate), [hd]);
  const openCenters = useMemo(
    () => Object.keys(CENTER_GATES).filter(c => !hd.definedCenters.includes(c)), [hd]);
  const gateName = (g: number) => (lang === 'en' ? GATE_EN[g] : GATE_TH[g]) || `Gate ${g}`;
  const phs = (v: string) => (lang === 'en' ? v : `${PHS_TH[v] || v}`);
  const authKey = Object.keys(AUTH_PROTOCOL).find(k => hd.authority.startsWith(k)) || 'Mental';

  const reveal = () => {
    if (!user) { onAuth && onAuth('signup'); return; }
    setRevealed(true);
    setUser({ ...user, hdType: typeKey, birth, btime: time, place, tz });
  };

  // Reveal only ever updated the in-memory user, so the birth data a member
  // typed evaporated with the tab: next login, next device, blank form again -
  // and the back office had nothing at all. Saving writes it into the profile
  // blob on the server, which is what the admin screens read.
  const [saveState, setSaveState] = useState<'idle' | 'busy' | 'done' | 'err'>('idle');
  const save = async () => {
    if (!user) { onAuth && onAuth('signup'); return; }
    setSaveState('busy');
    try {
      const r = await saveProfile({ birth, btime: time, place, tz, hdType: typeKey });
      if (r.user) setUser(r.user);
      setSaveState('done');
      setTimeout(() => setSaveState(s => (s === 'done' ? 'idle' : s)), 3000);
    } catch {
      setSaveState('err');
      setTimeout(() => setSaveState(s => (s === 'err' ? 'idle' : s)), 4000);
    }
  };

  // Native date/time inputs render in the BROWSER'S locale, and nothing on
  // the page can override that: the screenshot that prompted this showed
  // MM/DD/YYYY and an AM/PM clock to a Thai reader on a Thai page. Explicit
  // selects are the only rendering we control - day/month/year in the order
  // Thais actually write, and a 24-hour clock. The state underneath stays
  // "YYYY-MM-DD" and "HH:MM", so the astronomy, the save button and every
  // profile already stored are untouched.
  const [by, bm, bd] = birth.split('-').map(Number);
  const [hh, mi] = time.split(':').map(Number);
  const daysIn = (y: number, m: number) => new Date(y, m, 0).getDate();
  const setBirthDMY = (d: number, m: number, y: number) => {
    // Changing month or year can strand the day (31 Jan -> Feb): clamp it,
    // never silently roll into the next month the way Date() would.
    const dd = Math.min(d, daysIn(y, m));
    setBirth(y + '-' + String(m).padStart(2, '0') + '-' + String(dd).padStart(2, '0'));
    setRevealed(false);
  };
  const setTimeHM = (h: number, m: number) => {
    setTime(String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0'));
    setRevealed(false);
  };
  const MONTHS = lang === 'en'
    ? ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    : ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
  const NOW_Y = new Date().getFullYear();

  return (
    <div className="mm-page" data-screen-label="Human Design">
      <div className="mm-hd-hero glass">
        <div className="mm-hd-form">
          <h2>{tr('ถอดรหัสนักลงทุนในตัวคุณ', 'Decode the investor in you')}</h2>
          <p className="dim">{tr('ศาสตร์ Human Design วิเคราะห์พลังงานการตัดสินใจจากเวลาเกิดของคุณ เพื่อแนะนำสไตล์การลงทุนที่สอดคล้องกับธรรมชาติของตัวเอง', 'Human Design reads your decision-making energy from your birth time to suggest an investing style aligned with your nature')}</p>
          <div className="mm-field"><label>{tr('วันเกิด (วัน / เดือน / ปี ค.ศ. / พ.ศ.)', 'Birth date (day / month / year)')}</label>
            <div className="mm-hd-dmy mm-hd-date">
              <select className="mm-ipo-select" value={bd} aria-label={tr('วัน', 'Day')}
                onChange={e => setBirthDMY(Number(e.target.value), bm, by)}>
                {Array.from({ length: daysIn(by, bm) }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select className="mm-ipo-select" style={{ flex: 1 }} value={bm} aria-label={tr('เดือน', 'Month')}
                onChange={e => setBirthDMY(bd, Number(e.target.value), by)}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select className="mm-ipo-select" value={by} aria-label={tr('ปี', 'Year')}
                onChange={e => setBirthDMY(bd, bm, Number(e.target.value))}>
                {/* ค.ศ. with the พ.ศ. beside it: the stored value stays Gregorian
                    (the astronomy needs it), but a Thai reader knows their year
                    as 2537, not 1994, and making them subtract 543 in their
                    head is how wrong charts get generated. */}
                {Array.from({ length: NOW_Y - 1929 }, (_, i) => NOW_Y - i).map(y => (
                  <option key={y} value={y}>{lang === 'en' ? y : y + ' / ' + (y + 543)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mm-field"><label>{tr('เวลาเกิด (ระบบ 24 ชั่วโมง)', 'Birth time (24-hour)')}</label>
            <div className="mm-hd-dmy mm-hd-time">
              <select className="mm-ipo-select" value={hh} aria-label={tr('ชั่วโมง', 'Hour')}
                onChange={e => setTimeHM(Number(e.target.value), mi)}>
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
                ))}
              </select>
              <span className="mm-hd-colon mono">:</span>
              <select className="mm-ipo-select" value={mi} aria-label={tr('นาที', 'Minute')}
                onChange={e => setTimeHM(hh, Number(e.target.value))}>
                {Array.from({ length: 60 }, (_, m) => (
                  <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                ))}
              </select>
              <span className="dim mm-hd-colon">{tr('น.', 'h')}</span>
            </div>
          </div>
          <div className="mm-field"><label>{tr('เขตเวลาเกิด (UTC offset)', 'Birth timezone (UTC offset)')}</label>
            <select className="mm-ipo-select" value={tz} onChange={e => { setTz(Number(e.target.value)); setRevealed(false); }}>
              {/* Every offset carries a place name. Seven of these were bare
                numbers ("+4", "-6") that asked the reader to know their UTC
                offset by heart - the one thing this dropdown exists to spare
                them. Thailand stays first as the default audience; the rest
                run east to west. Myanmar is included because it is the
                neighbour Thai users actually pick, and its half-hour offset
                is exactly the kind nobody guesses. */}
            {([
              ['7', tr('ไทย/เวียดนาม +7', 'Thailand/Vietnam +7')],
              ['0', tr('อังกฤษ/UTC +0', 'UK/UTC +0')],
              ['1', tr('ยุโรปกลาง +1', 'Central Europe +1')],
              ['2', tr('ยุโรปตะวันออก/อียิปต์ +2', 'Eastern Europe/Egypt +2')],
              ['3', tr('มอสโก/ตุรกี/ซาอุฯ +3', 'Moscow/Turkey/Saudi +3')],
              ['3.5', tr('อิหร่าน +3:30', 'Iran +3:30')],
              ['4', tr('ดูไบ/UAE +4', 'Dubai/UAE +4')],
              ['5', tr('ปากีสถาน +5', 'Pakistan +5')],
              ['5.5', tr('อินเดีย +5:30', 'India +5:30')],
              ['6', tr('บังกลาเทศ +6', 'Bangladesh +6')],
              ['6.5', tr('พม่า +6:30', 'Myanmar +6:30')],
              ['8', tr('จีน/สิงคโปร์/มาเลเซีย +8', 'China/Singapore/Malaysia +8')],
              ['9', tr('ญี่ปุ่น/เกาหลี +9', 'Japan/Korea +9')],
              ['10', tr('ออสเตรเลียตะวันออก +10', 'Australia East +10')],
              ['-5', tr('สหรัฐฯ ตะวันออก -5', 'US Eastern -5')],
              ['-6', tr('สหรัฐฯ ตอนกลาง -6', 'US Central -6')],
              ['-7', tr('สหรัฐฯ เทือกเขา -7', 'US Mountain -7')],
              ['-8', tr('สหรัฐฯ แปซิฟิก -8', 'US Pacific -8')],
            ] as [string, string][]).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="mm-field"><label>{tr('สถานที่เกิด', 'Birth place')}</label><input type="text" value={place} onChange={e => setPlace(e.target.value)} /></div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="mm-btn primary big" onClick={reveal}>{user ? tr('✦ เปิดเผย Investor Bodygraph', '✦ Reveal Investor Bodygraph') : tr('✦ สมัครฟรีเพื่อเปิดเผย Bodygraph', '✦ Sign up free to reveal Bodygraph')}</button>
            {user && (
              <button className="mm-btn ghost big" onClick={save} disabled={saveState === 'busy'}>
                {saveState === 'busy' ? tr('กำลังบันทึก…', 'Saving…')
                  : saveState === 'done' ? tr('✓ บันทึกแล้ว', '✓ Saved')
                  : saveState === 'err' ? tr('บันทึกไม่สำเร็จ — ลองอีกครั้ง', 'Save failed — try again')
                  : tr('💾 บันทึกข้อมูลเกิด', '💾 Save birth data')}
              </button>
            )}
          </div>
          {user && saveState === 'idle' && !user.birth && (
            <p className="dim mm-footnote">{tr('บันทึกแล้วเปิดจากเครื่องไหนก็ได้ ไม่ต้องกรอกใหม่', 'Saved data follows your account to any device')}</p>
          )}
        </div>
        <div className={`mm-hd-graphwrap ${revealed ? 'revealed' : ''}`}>
          <div className="mm-hd-glow" style={{ background: `radial-gradient(circle, ${t.color}33, transparent 70%)` }}></div>
          {revealed
            ? <RaveBodygraph definedCenters={hd.definedCenters} designGates={designGates}
                personalityGates={persGates} en={lang === 'en'}></RaveBodygraph>
            : <Bodygraph typeKey="reflector" centers={[]}></Bodygraph>}
          {revealed && (
            <div className="mm-hd-legend2 mono" style={{ justifyContent: 'center', marginTop: 6 }}>
              <i style={{ background: DESIGN_C }}></i>{tr('Design (ร่างกาย)', 'Design (body)')}
              <i style={{ background: PERSONALITY_C }}></i>{tr('Personality (จิตสำนึก)', 'Personality (conscious)')}
            </div>
          )}
          {!revealed && <div className="mm-hd-veil"><span>✦</span>{tr('กรอกข้อมูลแล้วกดเปิดเผย', 'Fill in your details and reveal')}</div>}
        </div>
      </div>

      {revealed && (
        <div className="mm-hd-tabs">
          {([
            ['hd', 'Human Design', 'ผังพลังงาน'],
            ['bazi', 'BaZi 八字', 'สี่เสาชะตา'],
            ['iching', 'I Ching 易經', '64 เหอซ่าง'],
          ] as [string, string, string][]).map(([k, en, th]) => (
            <button key={k} className={`mm-hd-tab ${tab === k ? 'on' : ''}`} onClick={() => setTab(k as any)}>
              <b>{en}</b><span className="dim">{lang === 'en' ? '' : th}</span>
          </button>
          ))}
        </div>
      )}

      {revealed && tab === 'bazi' && (
        <div className="mm-card glass">
          <SecHead th="สี่เสาชะตา · BaZi (八字)" en="FOUR PILLARS · BaZi (八字)"
            right={<span className="mono dim">{tr('จากตำแหน่งดวงอาทิตย์จริง', 'from the real Sun')}</span>}></SecHead>
          <BaziPanel birthUTC={birthUTC} tzHours={tz}></BaziPanel>
        </div>
      )}

      {revealed && tab === 'iching' && (
        <div className="mm-card glass">
          <SecHead th="อี้จิง · 64 เหอซ่าง จากประตูของคุณ" en="I CHING · your 64 gates as hexagrams"
            right={<span className="mono dim">{hd.activeGates.length} {tr('ประตูทำงาน', 'active gates')}</span>}></SecHead>
          <IChingPanel personality={hd.personality} design={hd.design}></IChingPanel>
        </div>
      )}

      {revealed && tab === 'hd' && (
        <React.Fragment>
          <div className="mm-hd-result glass" style={{ borderColor: t.color + '55' }}>
            <div className="mm-hd-type">
              <span className="mm-hd-pop mono" style={{ color: t.color }}>{t.pop} {tr('ของประชากร', 'of population')}</span>
              <h2 style={{ color: t.color }}>{t.name}</h2>
              <span className="mm-hd-th">{t.th}</span>
              <div className="mm-hd-strat"><span>{tr('กลยุทธ์ชีวิต', 'Life strategy')}</span><b>{t.strategy}</b></div>
              <div className="mm-hd-strat"><span>{tr('ออร่า', 'Aura')}</span><b>{t.aura}</b></div>
            </div>
            <div className="mm-hd-desc">
              <h3>{tr('สไตล์นักลงทุนของคุณ', 'Your investor style')}</h3>
              <p>{t.investor}</p>
              <div className="mm-hd-cols">
                <div>
                  <h4 className="up">{tr('จุดแข็ง', 'Strengths')}</h4>
                  <ul>{t.strengths.map((s: string, i: number) => <li key={i}>{s}</li>)}</ul>
                </div>
                <div>
                  <h4 className="down">{tr('ข้อควรระวัง', 'Cautions')}</h4>
                  <ul>{t.cautions.map((s: string, i: number) => <li key={i}>{s}</li>)}</ul>
                </div>
              </div>
              <button className="mm-btn ghost" onClick={() => onNav('allocation')}>{tr('ดูพอร์ตแนะนำสำหรับ', 'See suggested portfolio for')} {t.name} →</button>
            </div>
          </div>

          <PremiumGate user={user} onUpgrade={onUpgrade} onAuth={onAuth} title={tr('ชาร์ตฉบับเต็ม · Chart Properties + Planetary Activations', 'Full chart · Properties + Planetary Activations')} desc={tr('Profile, Authority, Incarnation Cross และตำแหน่งดาวเคราะห์ทั้ง 13 ดวง (Design + Personality) — สำหรับสมาชิก Gold/Premium', 'Profile, Authority, Incarnation Cross and all 13 planetary positions (Design + Personality) — for Gold/Premium members')}>
          <div className="mm-grid-2">
            <div className="mm-card glass">
              <SecHead th="คุณสมบัติชาร์ต" en="CHART PROPERTIES"></SecHead>
              <div className="mm-hd-props">
                <div className="mm-hd-prop"><span>Type</span><b style={{ color: t.color }}>{t.name}</b></div>
                <div className="mm-hd-prop"><span>Strategy</span><b>{t.strategy}</b></div>
                <div className="mm-hd-prop"><span>Authority</span><b>{chart.authority}</b></div>
                <div className="mm-hd-prop"><span>Profile</span><b>{chart.profile} · {chart.profileName}</b></div>
                <div className="mm-hd-prop"><span>Definition</span><b>{chart.definition}</b></div>
                <div className="mm-hd-prop"><span>Incarnation Cross</span><b>{chart.cross}</b></div>
                <div className="mm-hd-prop"><span>Signature</span><b className="up">{chart.signature}</b></div>
                <div className="mm-hd-prop"><span>Not-Self Theme</span><b className="down">{chart.notSelf}</b></div>
                <div className="mm-hd-prop"><span>Defined Centers</span><b>{hd.definedCenters.length} / 9 {tr('ศูนย์', 'centers')}</b></div>
              </div>
            </div>
            <div className="mm-card glass">
              <SecHead th="ตำแหน่งดาวเคราะห์" en="PLANETARY ACTIVATIONS" right={<span className="mm-hd-legend2 mono"><i className="design"></i>Design <i className="pers"></i>Personality</span>}></SecHead>
              {/* Gate.line alone is a coordinate; the gate's NAME is the thing a
                  reader can act on, and colour/tone is what the Variables read. */}
              <table className="mm-act mono">
                <thead><tr>
                  <th></th><th>{tr('ดาว', 'Planet')}</th>
                  <th style={{ color: DESIGN_C }}>Design</th>
                  <th style={{ color: PERSONALITY_C }}>Personality</th>
                </tr></thead>
                <tbody>
                  {chart.personality.map((p: any, i: number) => {
                    const d: any = chart.design[i];
                    return (
                      <tr key={p.k}>
                        <td className="sym">{p.sym}</td>
                        <td>{lang === 'en' ? p.en : p.th}</td>
                        <td>
                          <span className="gate d">{d.gate}.{d.line}</span>
                          <span className="ct"> · {d.color}/{d.tone}</span>
                          <div className="nm">{gateName(d.gate)}</div>
                        </td>
                        <td>
                          <span className="gate p">{p.gate}.{p.line}</span>
                          <span className="ct"> · {p.color}/{p.tone}</span>
                          <div className="nm">{gateName(p.gate)}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="mm-footnote dim">{tr('Design = 88 วันก่อนเกิด (จิตใต้สำนึก) · Personality = ขณะเกิด (จิตสำนึก) · ตัวเลขหลังจุดคือ เส้น·สี/โทน', 'Design = 88 days before birth (subconscious) · Personality = at birth (conscious) · after the dot: line · colour/tone')}</p>
            </div>
          </div>

          <div className="mm-grid-2">
            {/* Variables / PHS — six properties read off the colour and tone of
                four specific activations. Nothing here is a guess. */}
            <div className="mm-card glass">
              <SecHead th="Variables · เงื่อนไขการใช้พลังงาน (PHS)" en="VARIABLES · PHS"
                right={<span className="mono dim">{hd.variables.code}</span>}></SecHead>
              <div className="mm-phs">
                {([
                  ['การย่อย/รับพลังงาน', 'Determination', hd.variables.determination],
                  ['การรับรู้', 'Cognition', hd.variables.cognition],
                  ['สภาพแวดล้อม', 'Environment', hd.variables.environment],
                  ['แรงจูงใจ', 'Motivation', hd.variables.motivation],
                  ['สัมผัสภายใน', 'Sense', hd.variables.sense],
                  ['มุมมอง', 'Perspective', hd.variables.perspective],
                ] as [string, string, string][]).map(([th, en, v]) => (
                  <div className="mm-phs-item" key={en}>
                    <span className="mono">{lang === 'en' ? en : th}</span>
                    <b>{v}</b>
                    {lang !== 'en' && PHS_TH[v] && <small>{PHS_TH[v]}</small>}
                  </div>
                ))}
              </div>
              <div className="mm-phs-code mono">
                <b>{hd.variables.code}</b>
                <span>{tr('P = Personality · D = Design · L/R = ซ้าย(รับรู้แบบยืดหยุ่น)/ขวา(แบบมีจุดโฟกัส)',
                  'P = Personality · D = Design · L/R = receptive / focused')}</span>
              </div>
              <p className="mm-footnote dim">{tr(
                'อ่านจากสีและโทนของดวงอาทิตย์และโหนดทั้งฝั่ง Design และ Personality — สภาพแวดล้อมคือที่ที่ควรนั่งดูจอ ไม่ใช่ที่ที่สวยที่สุด',
                'Read from the colour and tone of the Sun and Node on both sides — Environment is where you should sit to watch the screen, not the prettiest desk.')}</p>
            </div>

            {/* The open centres: the half of a chart that explains behaviour. */}
            <div className="mm-card glass">
              <SecHead th="ศูนย์ที่เปิด · จุดที่ตลาดเข้ามาในตัวคุณ" en="OPEN CENTRES · WHERE THE MARKET GETS IN"
                right={<span className="mono dim">{openCenters.length}/9 {tr('เปิด', 'open')}</span>}></SecHead>
              <div className="mm-open-c">
                {openCenters.length === 0 && <p className="dim">{tr('ทุกศูนย์ถูกกำหนด — คุณคงที่มาก แต่ก็ยืดหยุ่นตามสถานการณ์ได้น้อยกว่าคนอื่น', 'Every centre defined — highly consistent, and less able to bend to a situation than most.')}</p>}
                {openCenters.map(c => {
                  const r = OPEN_CENTER_READ[c];
                  return r ? (
                    <div className="mm-open-row" key={c}>
                      <i className="mono">{c.toUpperCase()}</i>
                      <span>{lang === 'en' ? r.en : r.th}</span>
                    </div>
                  ) : null;
                })}
              </div>
              {hd.hangingGates.length > 0 && (
                <React.Fragment>
                  <div className="mm-hd-prop" style={{ marginTop: 12 }}>
                    <span>{tr('ประตูค้าง (Hanging gates)', 'Hanging gates')}</span>
                    <b>{hd.hangingGates.length}</b>
                  </div>
                  <div className="mm-hang">
                    {hd.hangingGates.map((g: number) => (
                      <b key={g} title={gateName(g)}>{g} · {gateName(g)}</b>
                    ))}
                  </div>
                  <p className="mm-footnote dim">{tr(
                    'ประตูที่ติดอยู่ข้างเดียว — เป็นครึ่งช่องที่คุณถือไว้ และจะถูกดึงดูดไปหาคนที่ถือครึ่งที่เหลือ ในตลาดคือเหตุผลที่บางคน "ทำให้คุณกล้าเข้าไม้"',
                    'Gates lit at one end only — the half-channel you carry, and the reason certain people make you want to trade.')}</p>
                </React.Fragment>
              )}
            </div>
          </div>

          {/* Profile is the research-and-entry style; channels are the circuits
              that are actually complete in you. Both are read, not just listed. */}
          <div className="mm-grid-2">
            <div className="mm-card glass">
              <SecHead th="โปรไฟล์ · สไตล์การหาข้อมูลและเข้าไม้" en="PROFILE · how you research and enter"
                right={<span className="mono dim">{hd.profile}</span>}></SecHead>
              <p style={{ fontSize: '0.82rem', lineHeight: 1.65, margin: 0 }}>
                {PROFILE_READ[hd.profile]
                  ? (lang === 'en' ? PROFILE_READ[hd.profile].en : PROFILE_READ[hd.profile].th)
                  : hd.profileName}
              </p>
              <p className="mm-footnote dim">{hd.profileName}</p>
            </div>
            <div className="mm-card glass">
              <SecHead th="ช่องที่เชื่อมสมบูรณ์ · จุดแข็งที่คงที่" en="DEFINED CHANNELS · your fixed strengths"
                right={<span className="mono dim">{hd.definedChannels.length} / 36</span>}></SecHead>
              {hd.definedChannels.length === 0 ? (
                <p className="dim">{tr('ไม่มีช่องที่เชื่อมสมบูรณ์ — คุณสะท้อนสิ่งแวดล้อมทั้งหมด (Reflector)', 'No complete channel — you reflect the environment entirely (Reflector).')}</p>
              ) : (
                <div className="mm-chan-list">
                  {hd.definedChannels.map(([a, b]: [number, number]) => {
                    const k = channelKey(a, b);
                    const r = CHANNEL_READ[k];
                    return (
                      <div className="mm-chan-row" key={k}>
                        <b className="mono">{k}</b>
                        <span>{r ? (lang === 'en' ? r.en : r.th) : `${gateName(a)} ↔ ${gateName(b)}`}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* The decision protocol, which is the only part of a chart that
              changes what someone does on a Monday morning. */}
          <div className="mm-card glass" style={{ borderColor: t.color + '44' }}>
            <SecHead th="โปรโตคอลการตัดสินใจของคุณ" en="YOUR DECISION PROTOCOL"
              right={<span className="mono dim">{hd.authority}</span>}></SecHead>
            <p style={{ fontSize: '0.86rem', lineHeight: 1.65, margin: '0 0 10px' }}>
              {lang === 'en' ? AUTH_PROTOCOL[authKey].en : AUTH_PROTOCOL[authKey].th}
            </p>
            <div className="mm-hd-props">
              <div className="mm-hd-prop"><span>{tr('กลยุทธ์', 'Strategy')}</span><b>{t.strategy}</b></div>
              <div className="mm-hd-prop"><span>{tr('สัญญาณว่ามาถูกทาง', 'Signature')}</span><b className="up">{chart.signature}</b></div>
              <div className="mm-hd-prop"><span>{tr('สัญญาณว่าหลงทาง', 'Not-self')}</span><b className="down">{chart.notSelf}</b></div>
              <div className="mm-hd-prop"><span>{tr('ช่องที่เชื่อมสมบูรณ์', 'Defined channels')}</span><b>{hd.definedChannels.length} / 36</b></div>
              <div className="mm-hd-prop"><span>{tr('ประตูที่ทำงาน', 'Active gates')}</span><b>{hd.activeGates.length} / 64</b></div>
            </div>
          </div>

          {/* The structural half of the chart: circuitry, what Definition
              actually means here, every centre's gates, and the cross by name.
              All graph work over the 36 channels — see lib/hdStructure.ts. */}
          <HdStructure hd={hd} definedCenters={hd.definedCenters}></HdStructure>

          {/* Today's sky against this chart, and the 26 activations opened up
              one planet at a time — see lib/hdDetail.ts. */}
          <HdDetail hd={hd}></HdDetail>
          </PremiumGate>
        </React.Fragment>
      )}

      <div className="mm-card glass">
        <SecHead th="ทั้ง 5 พลังงานนักลงทุน" en="THE FIVE TYPES"></SecHead>
        <div className="mm-hd-types">
          {Object.entries(HD_TYPES).map(([k, v]: any) => (
            <div className={`mm-hd-typecard ${revealed && typeKey === k ? 'me' : ''}`} key={k} style={{ ['--tc' as any]: v.color }}>
              <b style={{ color: v.color }}>{v.name}</b>
              <span>{v.th} · {v.pop}</span>
              <p>{v.strategy}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- ALLOCATION ----------
export function AllocationPage({ user, onPick }: any) {
  const tr = useT();
  const { lang } = useLang();
  const al = (k: string) => (lang === 'en' ? (ALLOC_EN[k] || k) : k);
  const [typeKey, setTypeKey] = useState(user?.hdType || 'generator');
  const [risk, setRisk] = useState(3);
  const [capital, setCapital] = useState(100000);
  const t = HD_TYPES[typeKey];
  const items = useMemo(() => {
    const shift = (risk - 3) * 4;
    const raw = Object.entries(t.alloc).map(([k, v]: any) => {
      let nv = v;
      if (k === 'หุ้นเติบโต' || k === 'Crypto') nv = Math.max(2, v + shift);
      if (k === 'ตราสารหนี้' || k === 'ทอง') nv = Math.max(2, v - shift);
      return { k, v: nv, c: ALLOC_COLORS[k] };
    });
    const sum = raw.reduce((a, b) => a + b.v, 0);
    return raw.map(r => ({ ...r, v: Math.round(r.v / sum * 100) }));
  }, [typeKey, risk]);
  const expRet = (4.2 + risk * 1.6).toFixed(1);
  const expVol = (5 + risk * 3.2).toFixed(0);

  return (
    <div className="mm-page" data-screen-label="Asset Allocation">
      <div className="mm-grid-2">
        <div className="mm-card glass">
          <SecHead th="พอร์ตแนะนำตามพลังงานของคุณ" en="HD-ALIGNED PORTFOLIO"></SecHead>
          <div className="mm-alloc-controls">
            <div className="mm-field">
              <label>Human Design Type</label>
              <div className="mm-filterrow">
                {Object.entries(HD_TYPES).map(([k, v]: any) => (
                  <button key={k} className={`mm-pill ${typeKey === k ? 'on' : ''}`} style={typeKey === k ? { borderColor: v.color, color: v.color } : {}} onClick={() => setTypeKey(k)}>{v.name}</button>
                ))}
              </div>
            </div>
            <div className="mm-field">
              <label>{tr('ระดับความเสี่ยงที่รับได้', 'Risk tolerance')} <b className="mono">{(lang === 'en' ? ['Very low', 'Low', 'Medium', 'High', 'Very high'] : ['ต่ำมาก', 'ต่ำ', 'ปานกลาง', 'สูง', 'สูงมาก'])[risk - 1]}</b></label>
              <input type="range" min="1" max="5" step="1" value={risk} onChange={e => setRisk(+e.target.value)} className="mm-range" />
            </div>
            <div className="mm-field">
              <label>{tr('จำนวนเงินที่ใช้ลงทุน (บาท)', 'Capital to invest (THB)')}</label>
              <div className="mm-capital">
                <span className="mm-capital-cur mono">฿</span>
                <input type="number" min="0" step="1000" value={capital} onChange={e => setCapital(Math.max(0, +e.target.value))} className="mm-capital-input mono" />
              </div>
              <div className="mm-capital-quick">
                {[50000, 100000, 500000, 1000000].map(v => (
                  <button key={v} className={`mm-pill alt ${capital === v ? 'on' : ''}`} onClick={() => setCapital(v)}>฿{fmtNum(v / 1000, 0)}K</button>
                ))}
              </div>
            </div>
          </div>
          <div className="mm-alloc-stats">
            <div><span>{tr('ผลตอบแทนคาดหวัง / ปี', 'Expected return / yr')}</span><b className="mono up">+{expRet}%</b></div>
            <div><span>{tr('ความผันผวนคาดการณ์', 'Expected volatility')}</span><b className="mono gold">±{expVol}%</b></div>
            <div><span>{tr('เหมาะกับ', 'Suited for')}</span><b style={{ color: t.color }}>{t.name}</b></div>
          </div>
        </div>
        <div className="mm-card glass mm-center-col">
          <Donut items={items} size={210} thickness={26} centerTitle={`${items[0].v}%`} centerSub={al(items[0].k)}></Donut>
          <div className="mm-alloc-legend">
            {items.map(it => (
              <div key={it.k} className="mm-alloc-leg">
                <i style={{ background: it.c }}></i>
                <span>{al(it.k)}</span>
                <div className="mm-alloc-track"><div style={{ width: `${it.v}%`, background: it.c }}></div></div>
                <b className="mono">{it.v}%</b>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="mm-footnote dim">{tr('การจัดพอร์ตนี้เป็นแนวทางจำลองเพื่อการศึกษา ไม่ใช่คำแนะนำการลงทุน — ผลตอบแทนในอดีตไม่การันตีอนาคต', 'This allocation is a simulated guide for education, not investment advice — past performance does not guarantee future results')}</p>

      <div className="mm-card glass">
        <SecHead th="สินทรัพย์แนะนำในแต่ละหมวด" en="RECOMMENDED ASSETS BY CATEGORY" right={<span className="mm-chip">{tr('ตามสัดส่วนพอร์ตของคุณ', 'By your allocation')}</span>}></SecHead>
        <div className="mm-recos">
          {items.filter(it => it.v > 0).map(it => {
            const picks = (ALLOC_PICKS[it.k] || []).map(s => ASSETS.find(a => a.sym === s)).filter(Boolean) as any[];
            return (
              <div className="mm-reco-cat" key={it.k} style={{ ['--cc' as any]: it.c }}>
                <div className="mm-reco-cathead">
                  <span className="mm-reco-dot" style={{ background: it.c }}></span>
                  <b>{al(it.k)}</b>
                  <span className="mono dim">{it.v}% · ฿{fmtNum(capital * it.v / 100, 0)}</span>
                </div>
                <div className="mm-reco-list">
                  {picks.map(a => {
                    const cur = ({ SET: '฿', mai: '฿', BOND: '', JP: '¥', HK: 'HK$', EU: '€', IN: '₹', KR: '₩' } as any)[a.market] || '$';
                    const allocBaht = capital * it.v / 100 / (picks.length || 1);
                    return (
                      <button className="mm-reco-item wide" key={a.sym} onClick={() => onPick(a.sym)}>
                        <span className="mm-reco-badge mono" style={{ background: `${it.c}26`, color: it.c, borderColor: `${it.c}55` }}>{a.sym.slice(0, 4)}</span>
                        <span className="mm-reco-id"><b className="mono">{a.sym}</b><em>{a.name}</em></span>
                        <span className="mm-reco-alloc mono">฿{fmtNum(allocBaht, 0)}</span>
                        <span className="mm-reco-px">
                          <b className="mono">{cur}{fmtNum(a.price, a.price > 1000 ? 0 : 2)}</b>
                          <em className={`mono ${a.chg >= 0 ? 'up' : 'down'}`}>{a.chg >= 0 ? '+' : ''}{a.chg.toFixed(2)}%</em>
                        </span>
                        <span className="mm-reco-score mono" style={{ color: a.score >= 80 ? 'var(--up)' : a.score >= 65 ? 'var(--gold)' : 'var(--txt-dim)' }}>{a.score}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- AUTH MODAL ----------
export function AuthModal({ mode, onClose, onLogin, lineEnabled, googleEnabled }: any) {
  const t = useT();
  const [m, setM] = useState(mode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Local fallback when the server has no DB (accounts disabled) — keeps the
  // app fully usable offline, matching the original localStorage-only behavior.
  const localUser = () => m === 'signup'
    ? { name: name.trim() || email.split('@')[0], email, plan: 'premium', tier: 'Trial', trialEnd: Date.now() + 3 * 864e5 }
    : { name: name.trim() || email.split('@')[0], email, plan: 'free' };

  const submit = async (e: any) => {
    e.preventDefault();
    if (!email.includes('@')) { setErr(t('กรุณากรอกอีเมลให้ถูกต้อง', 'Please enter a valid email')); return; }
    // Length is a SIGNUP rule, not a login one. Checking it on login too would
    // lock out every customer who registered under the old 6-character minimum
    // and still has a password that short — the server does not check length
    // when logging in, and neither should this.
    if (m === 'signup' && pw.length < MIN_PASSWORD) { setErr(t(`รหัสผ่านต้องยาวอย่างน้อย ${MIN_PASSWORD} ตัวอักษร`, `Password must be at least ${MIN_PASSWORD} characters`)); return; }
    if (!pw) { setErr(t('กรุณากรอกรหัสผ่าน', 'Please enter your password')); return; }
    if (m === 'signup' && !name.trim()) { setErr(t('กรุณากรอกชื่อ', 'Please enter your name')); return; }
    setBusy(true); setErr('');
    try {
      const res = m === 'signup'
        ? await authSignup(email, pw, name.trim())
        : await authLogin(email, pw);
      onLogin(res.user, m === 'signup');
    } catch (e2: any) {
      const status = e2?.status;
      const code = e2?.body?.error;
      if (status === 503 || code === 'accounts_unavailable') {
        // No backend DB → seed a local-only account (offline mode).
        onLogin(localUser(), m === 'signup');
      } else if (code === 'email_taken') {
        setErr(t('อีเมลนี้ถูกใช้สมัครแล้ว — ลองเข้าสู่ระบบแทน', 'This email is already registered — try logging in'));
      } else if (code === 'invalid_credentials') {
        setErr(t('อีเมลหรือรหัสผ่านไม่ถูกต้อง', 'Incorrect email or password'));
      } else if (code === 'weak_password') {
        setErr(t(`รหัสผ่านต้องยาวอย่างน้อย ${MIN_PASSWORD} ตัวอักษร`, `Password must be at least ${MIN_PASSWORD} characters`));
      } else if (code === 'too_many_attempts') {
        // The login limiter. Saying "wrong password" here would send someone
        // who typed it correctly off to reset a password that was never wrong.
        const mins = Math.max(1, Math.ceil((e2?.body?.retryAfter || 900) / 60));
        setErr(t(`ลองผิดหลายครั้งเกินไป กรุณารออีก ${mins} นาทีแล้วลองใหม่`, `Too many attempts — please wait ${mins} minutes and try again`));
      } else if (code === 'bad_request') {
        setErr(t('ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบอีเมลและรหัสผ่าน', 'Invalid input — please check your email and password'));
      } else {
        setErr(t('เชื่อมต่อไม่สำเร็จ กรุณาลองใหม่', 'Connection failed — please try again'));
      }
    } finally { setBusy(false); }
  };
  return (
    <div className="mm-modal-bg" onClick={onClose}>
      <div className="mm-modal glass" onClick={e => e.stopPropagation()}>
        <button className="mm-modal-x" onClick={onClose}>✕</button>
        <div className="mm-modal-orb"></div>
        <h2>{m === 'login' ? t('ยินดีต้อนรับกลับ', 'Welcome back') : t('เริ่มต้นเส้นทางนักลงทุน', 'Start your investing journey')}</h2>
        <p className="dim">{m === 'login' ? t('เข้าสู่ระบบเพื่อดูพอร์ตและ Human Design Profile ของคุณ', 'Log in to see your portfolio and Human Design profile') : t('สมัครฟรี — รับสิทธิ์ Premium ครบทุกฟีเจอร์ ทดลองใช้ 3 วัน', 'Sign up free — full Premium, 3-day trial')}</p>
        {lineEnabled && (
          <button type="button" className="mm-btn mm-line-btn w100" onClick={() => { window.location.href = lineLoginUrl(); }}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M12 2C6.48 2 2 5.69 2 10.23c0 4.07 3.58 7.48 8.41 8.12.33.07.78.22.89.5.1.26.07.66.03.92l-.14.86c-.04.26-.2 1 .88.55 1.08-.46 5.83-3.43 7.96-5.88C21.36 13.78 22 12.06 22 10.23 22 5.69 17.52 2 12 2M8.04 12.9H6.06c-.29 0-.52-.23-.52-.52V8.43c0-.29.23-.52.52-.52s.52.23.52.52v3.43h1.46c.29 0 .52.23.52.52s-.23.52-.52.52m2.04-.52c0 .29-.23.52-.52.52s-.52-.23-.52-.52V8.43c0-.29.23-.52.52-.52s.52.23.52.52zm4.69 0c0 .22-.14.42-.36.49-.05.02-.11.03-.16.03-.16 0-.32-.08-.42-.22l-2.03-2.76v2.46c0 .29-.23.52-.52.52s-.52-.23-.52-.52V8.43c0-.22.14-.42.36-.49.06-.02.11-.03.17-.03.16 0 .31.08.41.22l2.04 2.77V8.43c0-.29.23-.52.52-.52s.51.23.51.52zm3.34-2.46c.29 0 .52.23.52.52s-.23.52-.52.52h-1.46v.91h1.46c.29 0 .52.24.52.52 0 .29-.23.52-.52.52h-1.98c-.29 0-.52-.23-.52-.52V8.43c0-.29.23-.52.52-.52h1.98c.29 0 .52.23.52.52s-.23.52-.52.52h-1.46v.91z"/></svg>
            {t('เข้าสู่ระบบด้วย LINE', 'Continue with LINE')}
          </button>
        )}
        {googleEnabled && (
          // Google's own four-colour mark, which their brand terms require on a
          // "Sign in with Google" button - a recoloured or redrawn G is not
          // allowed, so the paths carry their own fills rather than currentColor.
          <button type="button" className="mm-btn mm-google-btn w100" onClick={() => { window.location.href = googleLoginUrl(); }}>
            <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
              <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
              <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
              <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
              <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
            </svg>
            {t('เข้าสู่ระบบด้วย Google', 'Continue with Google')}
          </button>
        )}
        {(lineEnabled || googleEnabled) && (
          <div className="mm-auth-or"><span>{t('หรือใช้อีเมล', 'or use email')}</span></div>
        )}
        <form onSubmit={submit}>
          {m === 'signup' && (
            <div className="mm-field"><label>{t('ชื่อที่ใช้แสดง', 'Display name')}</label><input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t('เช่น Master Trader', 'e.g. Master Trader')} /></div>
          )}
          <div className="mm-field"><label>{t('อีเมล', 'Email')}</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" /></div>
          <div className="mm-field"><label>{t('รหัสผ่าน', 'Password')}</label><input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" /></div>
          {err && <div className="mm-err">{err}</div>}
          <button type="submit" className="mm-btn primary big w100" disabled={busy}>{busy ? t('กำลังดำเนินการ…', 'Working…') : (m === 'login' ? t('เข้าสู่ระบบ', 'Log in') : t('สมัครสมาชิกฟรี', 'Sign up free'))}</button>
        </form>
        <p className="mm-modal-legal dim">
          {t('การสมัครหรือเข้าสู่ระบบ ถือว่าคุณยอมรับ', 'By continuing you accept our')}{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">{t('ข้อตกลงการใช้งาน', 'Terms of Service')}</a>
          {' '}{t('และ', 'and')}{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">{t('นโยบายความเป็นส่วนตัว', 'Privacy Policy')}</a>
        </p>
        <div className="mm-modal-foot">
          {m === 'login' ? (
            <span>{t('ยังไม่มีบัญชี?', "Don't have an account?")} <button onClick={() => { setM('signup'); setErr(''); }}>{t('สมัครสมาชิก', 'Sign up')}</button></span>
          ) : (
            <span>{t('มีบัญชีอยู่แล้ว?', 'Already have an account?')} <button onClick={() => { setM('login'); setErr(''); }}>{t('เข้าสู่ระบบ', 'Log in')}</button></span>
          )}
        </div>
      </div>
    </div>
  );
}
